import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, CreditCard, Check, ArrowLeft, Plus, Tag, RefreshCw, AlertCircle } from 'lucide-react';
import { useStripeInvitation, InvitationBillingData } from '../../hooks/useStripeInvitation';
import { usePaymentMethod } from '../../hooks/Billing/usePaymentMethod';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';
import api from '../../redux/api';
import { toast } from 'react-toastify';

interface SendInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingData: InvitationBillingData;
  userId?: string;
  practiceId?: string; // For coupon validation
  currentMonthlyCost?: number | null; // Practice's current subscription total, for the "total after" line
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 50 },
  show: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.8, 
    y: 50,
    transition: {
      duration: 0.2
    }
  }
};

export const SendInviteModal: React.FC<SendInviteModalProps> = ({
  isOpen,
  onClose,
  billingData,
  userId,
  practiceId,
  currentMonthlyCost = null,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);
  const [skipEmailInvitation, setSkipEmailInvitation] = useState(false);
  const { processInvitationPayment, isProcessing, error } = useStripeInvitation(userId);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // New-subscription cost with any applied coupon, recomputed instantly
  const discountedTotal = appliedCoupon
    ? (appliedCoupon.discountType === 'amount'
        ? Math.max(0, billingData.totalAmount - appliedCoupon.discount)
        : billingData.totalAmount * (1 - appliedCoupon.discount / 100))
    : billingData.totalAmount;
  
  // Get customer ID from user data if available
  const getCustomerId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.stripe_customer_id || user?.customerId || undefined;
    } catch {
      return undefined;
    }
  };

  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.["sub"] || user?.sub || user?.userId || user?.id || undefined;
    } catch {
      return undefined;
    }
  }
  
  // Use Redux slice via hook instead of Zustand
  const { paymentMethods, isLoading: loadingPaymentMethods, fetch } = usePaymentMethod({
    userId: getUserId(),
    customerId: getCustomerId(), // Include customer ID if available
    autoFetch: false,
  });

  // Reset step and coupon when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedPaymentMethodId(null);
      setSkipEmailInvitation(false);
    } else {
      // Reset coupon state when modal closes
      setCouponCode('');
      setAppliedCoupon(null);
      setCouponError('');
      setSkipEmailInvitation(false);
    }
  }, [isOpen]);

  // Load payment methods when moving to step 2
  useEffect(() => {
    if (step === 2 && isOpen && userId) {
      fetch().then(() => {
        // Set default payment method if available
        const defaultMethod = paymentMethods.find(pm => pm.isDefault);
        if (defaultMethod) {
          setSelectedPaymentMethodId(defaultMethod.id);
        }
      }).catch((err) => {
        console.error('Error fetching payment methods:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isOpen, userId]); // fetch is stable from useCallback

  const handleAcceptAndSend = () => {
    // Move to step 2 (payment method selection)
    setStep(2);
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setSelectedPaymentMethodId(null);
  };

  const handleSelectPaymentMethod = (paymentMethodId: string) => {
    setSelectedPaymentMethodId(paymentMethodId);
  };

  // Validate coupon code with Stripe
  const validateCoupon = useCallback(async (code: string) => {
    setCouponError('');
    if (!code.trim()) {
      setCouponError('Please enter a coupon code');
      return false;
    }

    if (!practiceId) {
      const errorMsg = 'Practice ID is required for coupon validation. Please ensure you are logged in correctly.';
      setCouponError(errorMsg);
      console.error('❌ Practice ID missing for coupon validation:', {
        practiceId,
        billingDataPracticeId: billingData?.practiceId,
        localStorageUser: JSON.parse(localStorage.getItem('user') || '{}'),
      });
      toast.error(errorMsg);
      return false;
    }

    setIsValidatingCoupon(true);
    try {
      // Call backend API to validate Stripe promotion code
      const response = await api.post('/stripe/validate-promotion-code', {
        code: code.toUpperCase().trim(),
        practiceId,
        productId: billingData.productId // code must be associated with the product
      });
      
      const data = response.data?.body || response.data;
      
      if (data.valid && data.discount) {
        const coupon = { 
          code: code.toUpperCase(), 
          discount: data.discount,
          discountType: data.discountType || 'percentage',
          couponId: data.couponId,
          promotionCodeId: data.promotionCodeId
        };
        setAppliedCoupon(coupon);
        const discountText = data.discountType === 'amount' 
          ? `$${data.discount} off` 
          : `${data.discount}% off`;
        toast.success(`Coupon applied! ${discountText}`);
        return true;
      } else {
        setCouponError(data.message || 'Invalid coupon code');
        return false;
      }
    } catch (err: unknown) {
      console.error('Coupon validation error:', err);
      const error = err as { response?: { data?: { message?: string; body?: { message?: string } } }; message?: string };
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.body?.message
        || error.message 
        || 'Invalid coupon code';
      setCouponError(errorMessage);
      return false;
    } finally {
      setIsValidatingCoupon(false);
    }
  }, [practiceId, billingData.productId]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  }, []);

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMethodId) {
      toast.error('Please select a payment method');
      return;
    }

    // If a coupon code is entered but not validated, require validation
    if (couponCode.trim() && !appliedCoupon) {
      setCouponError('Please validate your coupon code before proceeding');
      toast.error('Please validate your coupon code before proceeding');
      return;
    }

    // Find the selected payment method to get the stripePaymentMethodId
    const selectedPaymentMethod = paymentMethods.find(pm => pm.id === selectedPaymentMethodId);
    
    if (!selectedPaymentMethod) {
      toast.error('Selected payment method not found');
      return;
    }

    // Use stripePaymentMethodId (Stripe ID) instead of MongoDB id
    const stripePaymentMethodId = selectedPaymentMethod.stripePaymentMethodId || selectedPaymentMethod.id;
    
    if (!stripePaymentMethodId) {
      toast.error('Payment method ID not found');
      return;
    }

    // Create updated billing data with coupon and payment method (using Stripe payment method ID)
    const updatedBillingData: InvitationBillingData = {
      ...billingData,
      couponCode: appliedCoupon?.code,
      paymentMethodId: stripePaymentMethodId, // Use Stripe payment method ID (pm_xxx)
    };

    // Snapshot billing details for post-payment finalization — a 3D Secure
    // redirect to the success page loses all React state.
    localStorage.setItem('pendingPurchaseBilling', JSON.stringify({
      productId: billingData.productId,
      priceId: billingData.priceId,
      totalAmount: Number(discountedTotal.toFixed(2)),
      couponCode: appliedCoupon?.code,
      paymentMethod: {
        type: selectedPaymentMethod.type,
        last4: selectedPaymentMethod.last4
      },
      timeStarted: Math.floor(Date.now() / 1000),
      skipInvitationEmail: skipEmailInvitation
    }));

    // Process payment with selected payment method and coupon.
    // On success Stripe redirects to the payment success page, which owns
    // finalization (creating clinicians, recording the transaction, sending
    // invitations) — so there is nothing to do here. The modal stays in its
    // processing state until the browser navigates. On failure the hook sets
    // `error`, which is rendered below.
    await processInvitationPayment(updatedBillingData);
  };

  const getCardIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'credit_card':
        return '💳';
      default:
        return '💳';
    }
  };

  const formatExpiryDate = (month?: number, year?: number) => {
    if (!month || !year) return '';
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  // Open the add payment method modal
  const handleAddPaymentMethodWithCheckout = () => {
    setIsAddingPaymentMethod(true);
  };

  const handlePaymentMethodAdded = async (paymentMethodId: string) => {
    setIsAddingPaymentMethod(false);
    // Refresh payment methods from Redux
    if (userId) {
      try {
        await fetch();
      } catch (err) {
        console.error('Error refreshing payment methods:', err);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center z-50 p-4 backdrop-blur-lg bg-black/20"
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="bg-white rounded-xl w-full max-w-md mx-4 p-8 relative"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {step === 1 ? (
                  <Send className="w-8 h-8 text-blue-600" />
                ) : (
                  <CreditCard className="w-8 h-8 text-blue-600" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {step === 1 ? 'Confirm New Clinicians' : 'Select Payment Method'}
              </h2>
              <p className="text-gray-600">
                {step === 1
                  ? 'Review the subscription cost and confirm to continue'
                  : 'Choose a payment method to complete your purchase'
                }
              </p>
              {/* Step indicator */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <div className={`w-2 h-2 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`w-2 h-2 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
              </div>
            </div>

            {/* Step 1 Content: Confirm — totals, coupon, email preference */}
            {step === 1 && (
              <div className="space-y-4 mb-6">
                {/* Selected team members */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Selected team members:</span>
                    <span className="text-lg font-bold text-blue-600">{billingData.inviteCount}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    {billingData.clinicianNames.slice(0, 3).join(', ')}
                    {billingData.clinicianNames.length > 3 && ` and ${billingData.clinicianNames.length - 3} more`}
                  </div>
                </div>

                {/* Cost summary */}
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">New subscriptions (est.):</span>
                    <span className={`text-sm font-medium ${appliedCoupon ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      ${billingData.totalAmount.toFixed(2)}/month
                    </span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-sm text-gray-600">After coupon ({appliedCoupon.code}):</span>
                      <span className="text-sm font-bold text-green-600">${discountedTotal.toFixed(2)}/month</span>
                    </div>
                  )}
                  <div className="text-xs text-gray-600 mt-1">
                    ${billingData.perInviteCost} per clinician
                  </div>
                  {currentMonthlyCost !== null && (
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-yellow-200">
                      <span className="text-sm font-semibold text-gray-800">Total after new subscriptions:</span>
                      <span className="text-lg font-bold text-yellow-700">
                        ${(currentMonthlyCost + discountedTotal).toFixed(2)}/month
                      </span>
                    </div>
                  )}
                  {currentMonthlyCost !== null && (
                    <div className="text-xs text-gray-500 mt-1">
                      Current subscriptions ${currentMonthlyCost.toFixed(2)} + new ${discountedTotal.toFixed(2)}
                    </div>
                  )}
                </div>

                {/* Coupon Code Section */}
                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    <Tag className="w-4 h-4 inline mr-1" />
                    Have a coupon code?
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code (e.g., SAVE20)"
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                      disabled={!!appliedCoupon || isValidatingCoupon}
                    />
                    {!appliedCoupon ? (
                      <button
                        onClick={() => validateCoupon(couponCode)}
                        disabled={isValidatingCoupon || !couponCode.trim()}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                      >
                        {isValidatingCoupon ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Validating...
                          </>
                        ) : (
                          'Apply'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={removeCoupon}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors whitespace-nowrap"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {couponError && (
                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {couponError}
                    </p>
                  )}
                  {appliedCoupon && (
                    <div className="mt-2 flex items-center gap-2 text-green-600 text-sm font-medium">
                      <Check className="w-4 h-4" />
                      <span>
                        Coupon "{appliedCoupon.code}" applied - {
                          appliedCoupon.discountType === 'amount'
                            ? `$${appliedCoupon.discount} off`
                            : `${appliedCoupon.discount}% off`
                        }
                      </span>
                    </div>
                  )}
                </div>

                {/* Email invitation preference */}
                <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipEmailInvitation}
                    onChange={(e) => setSkipEmailInvitation(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">Skip sending invitation by email</span>
                </label>
              </div>
            )}

            {/* Step 2 Content: Payment Method Selection */}
            {step === 2 && (
              <div className="space-y-4 mb-6">
                {/* Order Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">${billingData.totalAmount.toFixed(2)}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">
                        Discount ({appliedCoupon.code}):
                      </span>
                      <span className="text-sm font-medium text-green-600">
                        -{appliedCoupon.discountType === 'amount'
                          ? `$${appliedCoupon.discount.toFixed(2)}`
                          : `${appliedCoupon.discount}%`}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-base font-semibold text-gray-900">Total:</span>
                    <span className="text-lg font-bold text-blue-600">${discountedTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Payment Methods */}
                {loadingPaymentMethods ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="ml-3 text-gray-600">Loading payment methods...</span>
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-yellow-800 mb-3">No payment methods found</p>
                    <button 
                      onClick={handleAddPaymentMethodWithCheckout}
                      disabled={isAddingPaymentMethod || isProcessing}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAddingPaymentMethod ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Adding...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          <span>Add Payment Method</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">

                    {paymentMethods.map((method) => (
                      <motion.button
                        key={method.id}
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelectPaymentMethod(method.id);
                        }}
                        disabled={isProcessing || isAddingPaymentMethod}
                        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
                          selectedPaymentMethodId === method.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        } ${isProcessing || isAddingPaymentMethod ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">
                              {getCardIcon(method.type)}
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 capitalize">
                                  {method.type.replace('_', ' ')}
                                </span>
                                {method.isDefault && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                •••• •••• •••• {method.last4}
                              </p>
                              {method.expiryMonth && method.expiryYear && (
                                <p className="text-xs text-gray-500">
                                  Expires {formatExpiryDate(method.expiryMonth, method.expiryYear)}
                                </p>
                              )}
                            </div>
                          </div>
                          {selectedPaymentMethodId === method.id && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </motion.button>
                    ))}

                    {/* Add new payment method button */}
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddPaymentMethodWithCheckout();
                      }}
                      disabled={isAddingPaymentMethod || isProcessing}
                      className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center justify-center gap-2">
                        {isAddingPaymentMethod ? (
                          <>
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                            <span className="font-medium">Adding...</span>
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5" />
                            <span className="font-medium">Add new payment method</span>
                          </>
                        )}
                      </div>
                    </motion.button>
                  </div>
                )}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              {step === 1 ? (
                <>
                  <button
                    onClick={onClose}
                    disabled={isProcessing}
                    className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAcceptAndSend}
                    disabled={isProcessing}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    Confirm
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleBackToStep1}
                    disabled={isProcessing}
                    className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={handleConfirmPayment}
                    disabled={isProcessing || !selectedPaymentMethodId}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Complete Payment'
                    )}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Add Payment Method Modal */}
        <AddPaymentMethodModal
          isOpen={isAddingPaymentMethod}
          onClose={() => setIsAddingPaymentMethod(false)}
          onSuccess={handlePaymentMethodAdded}
          userId={userId}
        />
    </AnimatePresence>
  );
};
